import { Box, ClickAwayListener, MenuItem } from '@mui/material';
import {
  GridColumnsPanel,
  GridFilterPanel,
  GridToolbarContainer,
  GridToolbarContainerProps,
  useGridApiContext,
} from '@mui/x-data-grid-premium';
import { RiAlertLine, RiCalendarLine, RiFilter3Line, RiLayoutColumnLine } from '@remixicon/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { DateTime } from 'luxon';
import type { KeyboardEvent } from 'react';
import { FC, ReactNode, useEffect } from 'react';
import LuxonStaticDateRangePicker from 'src/components/atoms/buttons/LuxonStaticDateRangePicker';
import { ChipSelect } from 'src/components/atoms/ChipSelect';
import CountIcon from 'src/components/atoms/CountIcon';
import { MenuDropDown } from 'src/components/atoms/MenuDropDown';
import SearchBox from 'src/components/atoms/SearchBox';
import { AgButton } from 'src/components/ui';
import { usePersistentSearch } from 'src/providers/SearchBoxProvider';

export type ToolbarProps = {
  searchbarLabel?: string;
  useSearchbar?: boolean;
  children?: ReactNode;
  Buttons?: FC;
  BeforeButtons?: FC;
  handleSearch?: (hits: unknown) => void;
  searchBoxSlot?: ReactNode;
  onFilterClickedAway?: () => void;
  dateOptions?: { label: string; value: string }[];
} & GridToolbarContainerProps;

const defaultDateOptions = [
  { label: 'Updated Date', value: 'updatedAtTimestamp' },
  { label: 'Created Date', value: 'createdAtTimestamp' },
];

const toDateFilter = (dateType: string, start: DateTime | null, end: DateTime | null) => {
  if (!start) {
    return '';
  } else if (end) {
    return `${dateType}:${start.toMillis()} TO ${end.toMillis()}`;
  } else {
    return `${dateType}>${start.toMillis()}`;
  }
};

export type DateFilter = { start: DateTime | null; end: DateTime | null; type: string; filter: string };

const DateFilter = ({ dateOptions }: { dateOptions: Array<{ label: string; value: string }> }) => {
  const { filters, setFilters } = usePersistentSearch();
  const dateFilter = filters?.date as DateFilter;

  useEffect(() => {
    if (dateFilter?.type === undefined) {
      setFilters({
        filters,
        date: { ...(dateFilter || { start: null, end: null }), filter: '', type: dateOptions[0].value },
      });
    }
  });

  return (
    <MenuDropDown icon={<RiCalendarLine className="h-5 w-5" />} hint="Select date range">
      <Box sx={{ px: 2 }}>
        <ChipSelect fullWidth value={dateFilter?.type || dateOptions[0].value}>
          {dateOptions.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              onClick={() =>
                setFilters({ filters, date: { ...(dateFilter || { start: null, end: null }), type: option.value } })
              }
            >
              {option.label}
            </MenuItem>
          ))}
        </ChipSelect>
        <LuxonStaticDateRangePicker
          onChange={([start, end]) => {
            setFilters({ filters, date: { ...dateFilter, start, end } });
          }}
          value={[dateFilter?.start || null, dateFilter?.end || null]}
          calendars={1}
        />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <AgButton
            variant="white"
            className="w-1/2"
            onPress={() => {
              setFilters({ ...filters, date: { type: dateFilter?.type } });
            }}
          >
            Reset
          </AgButton>
          <AgButton
            className="w-1/2"
            onPress={() => {
              setFilters({
                ...filters,
                date: {
                  ...dateFilter,
                  filter: toDateFilter(
                    dateFilter?.type ?? dateOptions[0].value,
                    dateFilter?.start ?? null,
                    dateFilter?.end ?? null
                  ),
                },
              });
            }}
            variant="primary"
          >
            Save Date Filter
          </AgButton>
        </Box>
      </Box>
    </MenuDropDown>
  );
};

export default function Toolbar({
  searchbarLabel,
  useSearchbar, // prop based feature flag, remove when all pages have search
  children,
  Buttons,
  BeforeButtons,
  searchBoxSlot = <Box sx={{ flexGrow: 1 }} />,
  dateOptions,
  onFilterClickedAway = () => {},
  sx = {},
  ...props
}: ToolbarProps) {
  const flags = useFlags();
  const apiRef = useGridApiContext();
  const filters = apiRef.current?.state?.filter?.filterModel?.items?.length;

  return (
    <GridToolbarContainer
      {...props}
      sx={{ gap: 2, display: 'flex', pb: useSearchbar ? 2.5 : 1.5, ...sx, '@media print': { display: 'none' }, px: 0 }}
    >
      {useSearchbar ? <SearchBox label={searchbarLabel} size="sm" /> : searchBoxSlot}
      <Box sx={{ display: 'flex', gap: 1, height: '40px', alignItems: 'center' }}>
        {BeforeButtons && <BeforeButtons />}

        <MenuDropDown icon={<CountIcon count={filters} Icon={RiFilter3Line} />} hint="Filter rows">
          <ClickAwayListener onClickAway={onFilterClickedAway}>
            <GridFilterPanel
              filterFormProps={{
                valueInputProps: {
                  onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
                    e.stopPropagation();
                  },
                },
              }}
            />
          </ClickAwayListener>
        </MenuDropDown>
        {flags.orderFlagging && (
          <MenuDropDown icon={<RiAlertLine className="h-5 w-5" />} hint="Show flagged orders">
            warning
          </MenuDropDown>
        )}
        {flags.orderDateFilters && useSearchbar && <DateFilter dateOptions={dateOptions ?? defaultDateOptions} />}
        <MenuDropDown icon={<RiLayoutColumnLine className="h-5 w-5" />} hint="Show/Hide columns">
          <GridColumnsPanel sx={{ '& .MuiDataGrid-columnsPanelRow:has(>.Mui-disabled)': { display: 'none' } }} />
        </MenuDropDown>
        {Buttons && <Buttons />}
      </Box>
      {children}
    </GridToolbarContainer>
  );
}
